// SuperAdmin Session Tracer - captures breadcrumbs in a rolling buffer
// and flushes them to the database on crash or manual trigger

import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '@/lib/safeStorage';

const MAX_BREADCRUMBS = 200;
const MEMORY_INTERVAL_MS = 10_000;
const PERIODIC_FLUSH_MS = 60_000;
const TRACE_STORAGE_KEY = 'superadmin_trace_enabled';
const PENDING_BREADCRUMBS_KEY = 'superadmin_pending_breadcrumbs';

export interface Breadcrumb {
  timestamp: string;
  type: 'navigation' | 'click' | 'error' | 'network_error' | 'memory' | 'state_change';
  data: Record<string, unknown>;
}

interface DeviceInfo {
  userAgent: string;
  platform: string;
  screenSize: string;
  touchPoints: number;
  isMobile: boolean;
  isIOS: boolean;
}

function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return {
    userAgent: ua.substring(0, 500),
    platform: navigator.platform || 'unknown',
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    touchPoints: navigator.maxTouchPoints || 0,
    isMobile: isIOS || /Android|Mobile/.test(ua),
    isIOS,
  };
}

// Singleton buffer accessible from globalErrorHandler
let globalBreadcrumbs: Breadcrumb[] = [];
let globalSessionId: string | null = null;
let globalTraceActive = false;

export function getTraceBreadcrumbs(): Breadcrumb[] {
  return globalBreadcrumbs;
}

export function isTraceActive(): boolean {
  return globalTraceActive;
}

export function getTraceSessionId(): string | null {
  return globalSessionId;
}

function addBreadcrumb(crumb: Breadcrumb) {
  globalBreadcrumbs.push(crumb);
  if (globalBreadcrumbs.length > MAX_BREADCRUMBS) {
    globalBreadcrumbs = globalBreadcrumbs.slice(-MAX_BREADCRUMBS);
  }
}

export function useSuperAdminTracer() {
  const [isActive, setIsActive] = useState(false);
  const [traceId, setTraceId] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const intervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);
  const originalFetchRef = useRef<typeof fetch | null>(null);

  // Check if tracing is enabled for current user
  const checkTraceStatus = useCallback(async () => {
    const localFlag = safeLocalStorageGetItem(TRACE_STORAGE_KEY);
    if (localFlag !== 'true') {
      if (isActive) stopTracing();
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase.rpc('mcp_admin_tracer_get_active' as any, {
        p_user_id: user.id,
        p_org_ids: null,
      });

      const activeTrace = data?.data;

      if (activeTrace && !isActive) {
        setTraceId(activeTrace.id);
        startTracing(activeTrace.id);
      } else if (!activeTrace && isActive) {
        stopTracing();
      }
    } catch {
      // Silent fail
    }
  }, [isActive]);

  const startTracing = useCallback((activeTraceId: string) => {
    globalTraceActive = true;
    globalSessionId = sessionIdRef.current;
    globalBreadcrumbs = [];
    setIsActive(true);

    // Flush any pending breadcrumbs from previous session
    const pending = safeLocalStorageGetItem(PENDING_BREADCRUMBS_KEY);
    if (pending) {
      try {
        const parsed = JSON.parse(pending) as Breadcrumb[];
        globalBreadcrumbs.push(...parsed.slice(-50));
        safeLocalStorageSetItem(PENDING_BREADCRUMBS_KEY, '');
      } catch { /* ignore */ }
    }

    addBreadcrumb({
      timestamp: new Date().toISOString(),
      type: 'state_change',
      data: { key: 'trace_started', device: getDeviceInfo() },
    });

    // --- Click listener ---
    const clickHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'click',
        data: {
          tag: target.tagName,
          id: target.id || undefined,
          text: target.textContent?.substring(0, 80) || undefined,
          selector: getCssSelector(target),
        },
      });
    };
    document.addEventListener('click', clickHandler, { capture: true, passive: true });

    // --- Navigation listener ---
    const navHandler = () => {
      addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'navigation',
        data: { to: window.location.pathname + window.location.hash },
      });
    };
    window.addEventListener('popstate', navHandler);

    // --- Console.error intercept ---
    const originalError = console.error;
    console.error = (...args: unknown[]) => {
      addBreadcrumb({
        timestamp: new Date().toISOString(),
        type: 'error',
        data: {
          message: args.map(a => typeof a === 'string' ? a : String(a)).join(' ').substring(0, 500),
          source: 'console.error',
        },
      });
      originalError.apply(console, args);
    };

    // --- Fetch intercept for network errors ---
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch;
    }
    const origFetch = originalFetchRef.current;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      try {
        const response = await origFetch(...args);
        if (!response.ok && response.status >= 400) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || 'unknown';
          addBreadcrumb({
            timestamp: new Date().toISOString(),
            type: 'network_error',
            data: {
              url: url.substring(0, 200),
              status: response.status,
              method: (args[1] as RequestInit)?.method || 'GET',
            },
          });
        }
        return response;
      } catch (err) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || 'unknown';
        addBreadcrumb({
          timestamp: new Date().toISOString(),
          type: 'network_error',
          data: {
            url: url.substring(0, 200),
            method: (args[1] as RequestInit)?.method || 'GET',
            message: err instanceof Error ? err.message : String(err),
          },
        });
        throw err;
      }
    };

    // --- Memory snapshots ---
    const memoryPerf = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number } });
    if (memoryPerf.memory) {
      const memInterval = setInterval(() => {
        const mem = memoryPerf.memory;
        if (mem) {
          addBreadcrumb({
            timestamp: new Date().toISOString(),
            type: 'memory',
            data: {
              usedJSHeapSize: mem.usedJSHeapSize,
              totalJSHeapSize: mem.totalJSHeapSize,
              jsHeapSizeLimit: mem.jsHeapSizeLimit,
            },
          });
        }
      }, MEMORY_INTERVAL_MS);
      intervalsRef.current.push(memInterval);
    }

    // --- Periodic flush ---
    const flushInterval = setInterval(() => {
      flushBreadcrumbs(activeTraceId);
    }, PERIODIC_FLUSH_MS);
    intervalsRef.current.push(flushInterval);

    // --- beforeunload: save to localStorage ---
    const unloadHandler = () => {
      try {
        safeLocalStorageSetItem(PENDING_BREADCRUMBS_KEY, JSON.stringify(globalBreadcrumbs.slice(-50)));
      } catch { /* ignore */ }
    };
    window.addEventListener('beforeunload', unloadHandler);

    // Store cleanup refs
    (window as unknown as Record<string, unknown>).__traceCleanup = () => {
      document.removeEventListener('click', clickHandler, { capture: true } as EventListenerOptions);
      window.removeEventListener('popstate', navHandler);
      window.removeEventListener('beforeunload', unloadHandler);
      console.error = originalError;
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
      }
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];
    };
  }, []);

  const stopTracing = useCallback(async () => {
    globalTraceActive = false;
    setIsActive(false);
    safeLocalStorageSetItem(TRACE_STORAGE_KEY, 'false');

    // Run cleanup
    const cleanup = (window as unknown as Record<string, unknown>).__traceCleanup as (() => void) | undefined;
    cleanup?.();

    // Mark trace as completed
    if (traceId) {
      await flushBreadcrumbs(traceId, 'completed');
    }

    globalBreadcrumbs = [];
    setTraceId(null);
  }, [traceId]);

  const flushBreadcrumbs = useCallback(async (tId: string, status?: string) => {
    if (globalBreadcrumbs.length === 0 && !status) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const updateData: Record<string, unknown> = {
        p_user_id: user.id,
        p_trace_id: tId,
        p_breadcrumbs: globalBreadcrumbs,
        p_device_info: getDeviceInfo(),
        p_org_ids: null,
      };
      if (status && ['active', 'completed', 'crashed'].includes(status)) {
        updateData.p_status = status;
      }

      await supabase.rpc('mcp_admin_tracer_flush_breadcrumbs' as any, updateData);
    } catch {
      // Silent fail
    }
  }, []);

  const manualFlush = useCallback(async () => {
    if (traceId) {
      await flushBreadcrumbs(traceId);
    }
  }, [traceId, flushBreadcrumbs]);

  // Check on mount and auth changes
  useEffect(() => {
    checkTraceStatus();
    const interval = setInterval(checkTraceStatus, 30_000);
    return () => clearInterval(interval);
  }, [checkTraceStatus]);

  return { isActive, traceId, stopTracing, manualFlush };
}

function getCssSelector(el: HTMLElement): string {
  if (el.id) return `#${el.id}`;
  const tag = el.tagName.toLowerCase();
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.split(' ').filter(Boolean).slice(0, 2).join('.')
    : '';
  return (tag + cls).substring(0, 100);
}
